import Departments from './Departments';

const existingKidsSizes = {
  eu: { min: 23, max: 36, step: 1 },
  us: [
    { min: 7, max: 7, suffix: 'k' },
    { min: 7.5, max: 7.5, suffix: 'k' },
    { min: 8, max: 8, suffix: 'k' },
    { min: 8.5, max: 8.5, suffix: 'k' },
    { min: 9, max: 9, suffix: 'k' },
    { min: 9.5, max: 9.5, suffix: 'k' },
    { min: 10, max: 10, suffix: 'k' },
    { min: 10.5, max: 10.5, suffix: 'k' },
    { min: 11, max: 11, suffix: 'k' },
    { min: 11.5, max: 11.5, suffix: 'k' },
    { min: 12, max: 12, suffix: 'k' },
    { min: 12.5, max: 12.5, suffix: 'k' },
    { min: 13, max: 13, suffix: 'k' },
    { min: 13.5, max: 13.5, suffix: 'k' },
    { min: 1, max: 1, suffix: '' },
    { min: 1.5, max: 1.5, suffix: '' },
    { min: 2, max: 2, suffix: '' },
    { min: 2.5, max: 2.5, suffix: '' },
    { min: 3, max: 3, suffix: '' },
    { min: 3.5, max: 3.5, suffix: '' },
    { min: 4, max: 4, suffix: '' },
    { min: 4.5, max: 4.5, suffix: '' },
  ],
  uk: [
    { min: 6, max: 6, suffix: 'k' },
    { min: 6.5, max: 6.5, suffix: 'k' },
    { min: 7, max: 7, suffix: 'k' },
    { min: 7.5, max: 7.5, suffix: 'k' },
    { min: 8, max: 8, suffix: 'k' },
    { min: 8.5, max: 8.5, suffix: 'k' },
    { min: 9, max: 9, suffix: 'k' },
    { min: 9.5, max: 9.5, suffix: 'k' },
    { min: 10, max: 10, suffix: 'k' },
    { min: 10.5, max: 10.5, suffix: 'k' },
    { min: 11, max: 11, suffix: 'k' },
    { min: 11.5, max: 11.5, suffix: 'k' },
    { min: 12, max: 12, suffix: 'k' },
    { min: 12.5, max: 12.5, suffix: 'k' },
    { min: 13, max: 13, suffix: 'k' },
    { min: 13.5, max: 13.5, suffix: 'k' },
    { min: 1, max: 1, suffix: '' },
    { min: 1.5, max: 1.5, suffix: '' },
    { min: 2, max: 2, suffix: '' },
    { min: 2.5, max: 2.5, suffix: '' },
    { min: 3, max: 3, suffix: '' },
    { min: 3.5, max: 3.5, suffix: '' },
  ],
};

const existingBabiesSizes = {
  eu: { min: 16, max: 22, step: 1 },
  us: { min: 1, max: 6.5, step: 0.5, suffix: 'k' },
  uk: { min: 0, max: 5.5, step: 0.5, suffix: 'k' },
};

// the ranges here need to be equal for correct 1-1 mapping
const shoeSizesData = {
  [Departments.getMenInternalName()]: {
    it: { min: 36, max: 48.5, step: 0.5 },
    us: { min: 3, max: 15.5, step: 0.5 },
    uk: { min: 2, max: 14.5, step: 0.5 },
  },
  [Departments.getWomenInternalName()]: {
    it: { min: 34, max: 44, step: 0.5 },
    us: { min: 4, max: 14, step: 0.5 },
    uk: { min: 1, max: 11, step: 0.5 },
    fr: { min: 35, max: 45, step: 0.5 },
  },
  [Departments.getBoysInternalName()]: existingKidsSizes,
  [Departments.getGirlsInternalName()]: existingKidsSizes,
  [Departments.getBabyBoysInternalName()]: existingBabiesSizes,
  [Departments.getBabyGirlsInternalName()]: existingBabiesSizes,
  [Departments.getJuniorKidsDepartmentsInternalNames()]: existingBabiesSizes,
};

function generateSizes(min, max, step, suffix) {
  const arr = [];
  for (let it = min; it <= max; it += step) {
    arr.push({ min: it, max: it, suffix: suffix || '' });
  }

  return arr;
}

class ShoeSizeData {
  static all() {
    let arr = [];

    if (!shoeSizesData) {
      return [];
    }
    Object.keys(shoeSizesData).forEach(departmentProp => {
      const department = shoeSizesData[departmentProp];
      if (department) {
        Object.keys(department).forEach(countryProp => {
          const country = department[countryProp];

          let sizes = [];
          // process individual entries
          if (Array.isArray(country)) {
            sizes = country.map(obj => {
              return new ShoeSizeData(countryProp, departmentProp, obj);
            });
          } else {
            // generate array
            sizes = generateSizes(country.min, country.max, country.step, country.suffix).map(size => {
              return new ShoeSizeData(countryProp, departmentProp, size);
            });
          }
          arr = arr.concat(sizes);
        });
      }
    });
    return arr;
  }

  // groups sorted by size
  // each group is an array of sizes
  static groupedByCountry(departmentName) {
    const groupsCount = ShoeSizeData.countriesISO(departmentName).length;
    const all = ShoeSizeData.filtered(null, departmentName);
    const groupSize = all.length / groupsCount;

    return all.reduce((acc, el, idx) => {
      let group = acc[idx % groupSize];
      if (group) {
        group.push(el);
      } else {
        group = [el];
      }
      acc[idx % groupSize] = group;
      return acc;
    }, []);
  }

  // in kids & babies there is no 1-1 mapping between countries so the only size equal to eg. 3k US is itself
  static allEqual(shoeSizeData) {
    if (Departments.getKidsDepartmentsInternalNames().includes(shoeSizeData.departmentName)) {
      return [shoeSizeData];
    }

    const grouped = ShoeSizeData.groupedByCountry(shoeSizeData.departmentName);

    const string = shoeSizeData.toStringRepresentation();

    for (const group of grouped) {
      const matches = group.filter(sizeData => sizeData.toStringRepresentation() === string);
      if (matches.length > 0) {
        return group;
      }
    }

    return null;
  }

  // if either value is null that part of filtering is not applied
  static filtered(iso, departmentName) {
    const all = this.all();

    if (!iso && !departmentName) {
      return all;
    }

    let filtered = all;

    if (iso) {
      filtered = filtered.filter(el => el.countryISO === iso);
    }

    if (departmentName) {
      filtered = filtered.filter(el => el.departmentName === departmentName);
    }

    return filtered;
  }

  static countriesISO(department) {
    const countriesData = shoeSizesData[department];
    const arr = [];

    for (const countryProp in countriesData) {
      if (countriesData.hasOwnProperty(countryProp)) {
        arr.push(countryProp);
      }
    }

    return arr;
  }

  constructor(countryISO, departmentName, size) {
    this.countryISO = countryISO;
    this.departmentName = departmentName;
    this.size = size;
  }

  toDisplayString() {
    if (this.size.min !== this.size.max) {
      return `${this.size.min} - ${this.size.max}${this.size.suffix}`;
    }
    return `${this.size.min}${this.size.suffix}`;
  }

  toLongDisplayString() {
    return `${this.toDisplayString()} ${this.countryISO.toUpperCase()}`;
  }

  // serializes to eg 'BSHOE_UK_1.5-2k', 'BSHOE_US_2.5-3k', 'BSHOE_EU_18'
  toStringRepresentation() {
    let departmentPrefix = '';
    if (Departments.getAdultsDepartmentsInternalNames().includes(this.departmentName)) {
      departmentPrefix = this.departmentName === Departments.getWomenInternalName() ? 'F' : 'M';
    } else {
      departmentPrefix = 'K';
    }

    let base = '';
    const normalizedSuffix = this.size.suffix.toUpperCase();
    if (this.size.min !== this.size.max) {
      base = `${this.size.min}-${this.size.max}${normalizedSuffix}`;
    } else {
      base = `${this.size.min}${normalizedSuffix}`;
    }

    return `${departmentPrefix}SHOE_${this.countryISO.toUpperCase()}_${base}`;
  }

  static fromStringRepresentation(string, department) {
    return parseAdultShoeSize(string, department) || parseKidsShoeSize(string, department);
  }
}

const parseAdultShoeSize = (string, department) => {
  const regex = /(F|M)SHOE_(.*)_(\d+\.?\d{0,2})/;

  const found = string.match(regex);
  if (!found) {
    return null;
  }

  const countryISO = found[2].toLowerCase();
  const size = Number(found[3]);
  return new ShoeSizeData(countryISO, department, { min: size, max: size, suffix: '' }); // no ranges for adults atm
};

const parseKidsShoeSize = (string, department) => {
  const regex = /(K)SHOE_(.*)_([^K]*)(K?)/;

  const found = string.match(regex);
  if (!found) {
    return null;
  }

  const countryISO = found[2].toLowerCase();
  const sizeString = found[3];
  const suffix = (found[4] || '').toLowerCase();
  const rangeRegex = /(.*)-(.*)/;
  const foundRange = sizeString.match(rangeRegex);
  if (foundRange) {
    return new ShoeSizeData(countryISO, department, {
      min: parseFloat(foundRange[1]),
      max: parseFloat(foundRange[2]),
      suffix,
    });
  }
  return new ShoeSizeData(countryISO, department, {
    min: parseFloat(sizeString),
    max: parseFloat(sizeString),
    suffix,
  });
};

export { ShoeSizeData };
